import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { handleDynamicListCreation } from '../utils/handleListCreation';
import CreateButtonTooltipHeading from './CreateButtonTooltipHeading';
import CreateButtonTooltipTitle from './CreateButtonTooltipTitle';
import CustomPropertiesForm from './CustomPropertiesForm';
import { SAVE_NEW_LIST_MUTATION } from '../data/queries';
import { Alert } from 'foundations-components/transitional/components/Alert';
import I18n from 'I18n';
import { RemoveIcon, SuccessIcon } from 'foundations-assets';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ListCreationModal({
  processingType,
  filterBranch,
  onSuccess,
  onFailure,
  onCancel,
  getIsUngated: __getIsUngated,
  objectTypeId = '0-1'
}) {
  const [customProperties, setCustomProperties] = useState({});
  const [customPropertiesStatus, setCustomPropertiesStatus] = useState();
  const [listName, setListName] = useState('');
  const [listCreationFail, setListCreationFail] = useState(false);
  const [errorCode, setErrorCode] = useState();
  const onCustomPropertiesChange = useCallback(changes => {
    setCustomProperties(changes);
  }, []);
  const [saveList, {
    loading
  }] = useMutation(SAVE_NEW_LIST_MUTATION, {
    onCompleted: response => {
      if (response.createListDefinition.userErrors && response.createListDefinition.userErrors.length) {
        setListCreationFail(true);
        setErrorCode(response.createListDefinition.userErrors[0].code);
        if (onFailure) {
          onFailure();
        }
      } else {
        setListCreationFail(false);
        if (onSuccess) {
          onSuccess(response.createListDefinition);
        }
      }
    }
  });
  const handleListCreation = () => {
    if (processingType === 'DYNAMIC') {
      handleDynamicListCreation(objectTypeId, filterBranch, customProperties);
    } else {
      const transformedCustomProperties = Object.keys(customProperties).map(property => ({
        name: property,
        value: customProperties[property]
      }));
      const portalId = PortalIdParser.get();
      if (portalId) {
        saveList({
          variables: {
            customProperties: transformedCustomProperties,
            filterBranch: filterBranch || null,
            name: listName,
            objectTypeId,
            processingType: 'MANUAL',
            portalId: portalId.toString(),
            listPermissions: {}
          }
        }).catch(err => {
          Raven.captureException(err);
        });
      }
    }
  };
  const onListNameChange = ({
    target: {
      value
    }
  }) => {
    setListName(value);
  };
  const invalidCustomPropertyName = (() => {
    if (!customPropertiesStatus || !customPropertiesStatus.errors || !customPropertiesStatus.errors.get('MISSING_REQUIRED_PROPERTY') || !customPropertiesStatus.errors.get('MISSING_REQUIRED_PROPERTY').length) {
      return undefined;
    }
    return customPropertiesStatus.errors.get('MISSING_REQUIRED_PROPERTY')[0].label || customPropertiesStatus.errors.get('MISSING_REQUIRED_PROPERTY')[0].name;
  })();
  const createButtonDisabled = Boolean(!listName && processingType === 'STATIC' || invalidCustomPropertyName || customPropertiesStatus && !customPropertiesStatus.saveable);

  //We don't show the name field for DYNAMIC list creation, so we should not show the error message
  const listNameIsValid = Boolean(listName) || processingType === 'DYNAMIC';
  return /*#__PURE__*/_jsxs(Modal, {
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "segmentsLib.listCreationModal.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => onCancel()
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [listCreationFail && /*#__PURE__*/_jsx(Alert, {
        "data-test-id": "error-alert",
        titleText: I18n.text('segmentsLib.listCreationModal.errors.title'),
        type: "danger",
        children: errorCode === 'DUPLICATE_LIST_NAMES' ? I18n.text('segmentsLib.listCreationModal.errors.duplicatedListName') : I18n.text('segmentsLib.listCreationModal.errors.general')
      }), processingType === 'STATIC' && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "segmentsLib.listCreationModal.nameInput"
        }),
        children: /*#__PURE__*/_jsx(TextInput, {
          "data-test-id": "list-name-input",
          onChange: onListNameChange
        })
      }), /*#__PURE__*/_jsx(CustomPropertiesForm, {
        onChange: onCustomPropertiesChange,
        onStatusChange: setCustomPropertiesStatus,
        values: customProperties
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        "data-test-id": "create-list-button-tooltip",
        disabled: !createButtonDisabled,
        headingText: /*#__PURE__*/_jsx(CreateButtonTooltipHeading, {
          listNameIsValid: listNameIsValid,
          invalidCustomPropertyName: invalidCustomPropertyName,
          customPropertiesInvalid: Boolean(customPropertiesStatus && customPropertiesStatus.invalid)
        }),
        title: /*#__PURE__*/_jsx(CreateButtonTooltipTitle, {
          listNameIsValid: listNameIsValid,
          invalidCustomPropertyName: invalidCustomPropertyName,
          customPropertiesInvalid: Boolean(customPropertiesStatus && customPropertiesStatus.invalid)
        }),
        children: /*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": "create-list-button",
          use: "primary",
          disabled: createButtonDisabled,
          onClick: handleListCreation,
          loading: loading,
          resultIcon: listCreationFail ? RemoveIcon : SuccessIcon,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "segmentsLib.listCreationModal.createButton"
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        "data-test-id": "cancel-button",
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "segmentsLib.listCreationModal.cancelButton"
        })
      })]
    })]
  });
}